<template>
  <vs-col vs-lg="12" vs-sm="12" vs-xs="12" class="mb-8 club-settings">
    <p class="club-settings__lead">
      انتخاب کنید مشتری چطور عضو باشگاه این مجموعه شود.
    </p>

    <div class="club-settings__options" role="radiogroup">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        role="radio"
        class="club-settings__option"
        :class="{ 'club-settings__option--selected': mode === option.value }"
        :aria-checked="mode === option.value ? 'true' : 'false'"
        :disabled="loading"
        @click="select(option.value)"
      >
        <span class="club-settings__dot">
          <span v-if="mode === option.value" class="club-settings__dot-fill" />
        </span>

        <span class="club-settings__copy">
          <span class="club-settings__title">{{ option.title }}</span>
          <span class="club-settings__description">
            {{ option.description }}
          </span>
        </span>
      </button>
    </div>

    <div v-if="loading" class="club-settings__saving">
      <Loader color="#232323" size="7px" />
      <span>در حال ذخیره تغییرات…</span>
    </div>
  </vs-col>
</template>

<script>
  import { mapGetters } from "vuex";
  import productApiService from "../../services/api/ProductApiService";
  import Loader from "../../components/Loader.vue";

  const CLUB_ENABLED = "club-enabled";
  const CLUB_REGISTRATION_REQUIRED = "club-registration-required";

  /**
   * `value` comes back as `"1"`, `1` or `true` depending on the endpoint, and
   * `"0"` is truthy as a string.
   */
  function isOn(value) {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;

    return value !== "" && value !== "0" && value !== "false";
  }

  export default {
    name: "ClubSettings",
    components: { Loader },
    data() {
      return {
        loading: false,
        // What the operator picked, held separately so a failed write can put
        // the selection back where the server still has it.
        selected: null,
      };
    },
    computed: {
      ...mapGetters({
        currentProvider: "profile/currentProvider",
      }),
      enabledSetting() {
        return this.items.find((item) => item.key === CLUB_ENABLED);
      },
      registrationSetting() {
        return this.items.find(
          (item) => item.key === CLUB_REGISTRATION_REQUIRED
        );
      },
      persistedMode() {
        if (!this.enabledSetting || !isOn(this.enabledSetting.value)) {
          return "off";
        }

        return this.registrationSetting && isOn(this.registrationSetting.value)
          ? "required"
          : "optional";
      },
      mode() {
        return this.selected || this.persistedMode;
      },
      options() {
        return [
          {
            value: "off",
            title: "غیرفعال",
            description: "باشگاه مشتریان برای این مجموعه نمایش داده نمی‌شود.",
          },
          {
            value: "optional",
            title: "عضویت اختیاری",
            description:
              "مشتری می‌تواند عضو شود یا بدون ثبت‌نام وارد منو شود.",
          },
          {
            value: "required",
            title: "عضویت اجباری",
            description: "مشتری باید پیش از مشاهده منو ثبت‌نام کند.",
          },
        ];
      },
    },
    methods: {
      write(setting, on) {
        if (!setting || isOn(setting.value) === on) return Promise.resolve();

        return productApiService
          .storeOrEditShopSettings(setting.id, on, this.currentProvider.id)
          .then(() => {
            // The list this card reads from is the parent's, so the new value
            // has to land on the item itself or the card reverts on re-render.
            setting.value = on ? "1" : "0";
          });
      },
      select(next) {
        if (this.loading || next === this.mode) return;
        if (!this.enabledSetting || !this.registrationSetting) return;

        const previous = this.persistedMode;

        this.selected = next;
        this.loading = true;

        const clubOn = next !== "off";
        const registrationOn = next === "required";

        /**
         * Two writes, ordered so an interrupted pair can never leave the
         * collection requiring a registration for a club that is switched off.
         * Turning the requirement on goes last; turning it off goes first.
         */
        const sequence = registrationOn
          ? () =>
              this.write(this.enabledSetting, clubOn).then(() =>
                this.write(this.registrationSetting, registrationOn)
              )
          : () =>
              this.write(this.registrationSetting, registrationOn).then(() =>
                this.write(this.enabledSetting, clubOn)
              );

        sequence()
          .then(() => {
            this.$vs.notify({
              text: "تنظیمات باشگاه مشتریان ذخیره شد.",
              color: "success",
              position: "bottom-center",
            });
          })
          .catch(() => {
            this.selected = previous;
            this.$vs.notify({
              text: "تنظیمات باشگاه مشتریان ذخیره نشد. دوباره تلاش کنید.",
              color: "danger",
              position: "bottom-center",
            });
          })
          .finally(() => {
            this.loading = false;
          });
      },
    },
    props: {
      items: {
        type: Array,
        required: true,
      },
    },
  };
</script>

<style scoped lang="scss">
  .club-settings {
    &__lead {
      margin: 0 0 1rem;
      font-size: 0.85rem;
      color: #626262;
    }

    &__options {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 1rem;

      @media (max-width: 820px) {
        grid-template-columns: 1fr;
      }
    }

    &__option {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 1rem;
      text-align: start;
      background: transparent;
      border: 1px solid #e4e4e4;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: border-color 0.2s ease, background-color 0.2s ease;

      &:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.02);
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }

      &--selected {
        border-color: rgba(var(--vs-primary), 1);
        background: rgba(var(--vs-primary), 0.08);
      }
    }

    &__dot {
      display: grid;
      place-items: center;
      flex: 0 0 auto;
      width: 1rem;
      height: 1rem;
      margin-top: 0.2rem;
      border: 2px solid #cfcfcf;
      border-radius: 50%;

      .club-settings__option--selected & {
        border-color: rgba(var(--vs-primary), 1);
      }
    }

    &__dot-fill {
      width: 0.5rem;
      height: 0.5rem;
      background: rgba(var(--vs-primary), 1);
      border-radius: 50%;
    }

    &__copy {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__title {
      font-weight: 600;
    }

    &__description {
      font-size: 0.75rem;
      color: #626262;
    }

    &__saving {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.75rem;
      font-size: 0.75rem;
      color: #626262;
    }
  }
</style>
